import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  PlanReviewTestCart1,
  PlanReviewTestCurrentPlan,
  PlanReviewTestInvalidCart,
  PlanReviewTestUpsellCart,
  PlanReviewTestValidCart,
} from '@app/test-ssn/testData/plan-review/plan-review-test-data';
import { getBaggageTestData } from '@app/test-ssn/testData/services-application/baggageApplicationModalTestData';
import { getLoungeTestData } from '@app/test-ssn/testData/services-application/loungeApplicationModalTest';
import { getMealTestData } from '@app/test-ssn/testData/services-application/mealApplicationModalTestData';
import { ANA_BIZ_OFFICE_CODE } from '@common/interfaces';
import { apiEventAll, deepCopy, isStringEmpty } from '@common/helper';
import {
  CreateCartStoreService,
  CurrentCartStoreService,
  CurrentPlanStoreService,
  DeliveryInformationStoreService,
  LocalPlanService,
} from '@common/services';
import { CurrentCartState, CurrentPlanModel } from '@common/store';
import { RoutesResRoutes } from '@conf/routes.config';
import { SupportComponent } from '@lib/components/support-class';
import { LoginStatusType } from '@lib/interfaces';
import { CommonLibService } from '@lib/services';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'asw-plan-review-test',
  templateUrl: './plan-review-test.component.html',
  styleUrls: ['../../test-ssn.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanReviewTestComponent extends SupportComponent implements OnInit {
  public text = '';

  public testDemo1Number = 1;
  public testDemo2Number = 1;

  reload(): void {}
  init(): void {}
  destroy(): void {}

  constructor(
    private _common: CommonLibService,
    private _router: Router,
    private _deliveryInformationStoreService: DeliveryInformationStoreService,
    private _currentCart: CurrentCartStoreService,
    private _currentPlan: CurrentPlanStoreService,
    private _createCartStoreService: CreateCartStoreService,
    private _translateService: TranslateService,
    private _localPlanService: LocalPlanService
  ) {
    super(_common);
  }

  /**
   * プラン確認デモモード
   * @param num 任意のID
   */
  planReviewTestDemo(num: string = '01') {
    this._currentCart.resetCurrentCart();
    this._currentPlan.resetCurrentPlan();
    apiEventAll(
      () => {
        this._createCartStoreService.setCreateCartFromApi({
          airOfferId: 'TESTDEMOCART_FY25_' + num,
        });
      },
      this._createCartStoreService.getCreateCart$(),
      (data) => {
        this._common.aswCommonStoreService.updateAswCommon({ functionId: 'R01', pageId: 'P030' });
        this._deliveryInformationStoreService.updateDeliveryInformation({
          planReviewInformation: {
            isNeedGetCart: false,
          },
        });
        this._currentCart.setCurrentCart(data);
        this._sleep(() => {
          this._router.navigateByUrl(RoutesResRoutes.PLAN_REVIEW);
        });
      }
    );
  }

  /**
   * 指定ミリ秒後に後続処理を実行
   * @param nextEvent 後続処理
   * @param ms 待機時間(ミリ秒)
   */
  private _sleep(nextEvent: () => void, ms: number = 500): void {
    setTimeout(nextEvent, ms);
  }

  /**
   * 空席照会結果画面からの遷移を再現
   * @param page
   * @param func
   * @param isValid
   */
  planReviewTestFromPrevPage(page: string, func: string, isValid: boolean) {
    this._common.aswContextStoreService.updateAswContextByKey('loginStatus', LoginStatusType.NOT_LOGIN);
    this._common.aswContextStoreService.updateAswContextByKey('posCountryCode', 'JP');
    this._common.aswContextStoreService.updateAswContextByKey('lang', 'ja');
    this._translateService.use('ja');
    this._common.aswContextStoreService.updateAswContextByKey('deviceType', 'PC');

    this._deliveryInformationStoreService.setDeliveryInformationByKey('planReviewInformation', 'isNeedGetCart', false);
    const cart = isValid ? PlanReviewTestValidCart : PlanReviewTestInvalidCart;
    this._currentCart.setCurrentCart(cart);
    this.planReviewTest(page, func);
  }

  /**
   * プランリスト画面からの遷移を再現
   */
  planReviewTestFromPlanList() {
    this._currentCart.setCurrentCart(PlanReviewTestValidCart);
    this._currentPlan.setCurrentPlan(PlanReviewTestCurrentPlan);
    this._deliveryInformationStoreService.setDeliveryInformationByKey('planReviewInformation', 'isNeedGetCart', false);
    this.planReviewTest('P042', 'R01');
  }

  /**
   * 後続画面からの遷移を再現
   * @param page
   * @param func
   */
  planReviewTestFromLater(page: string, func: string) {
    this._currentPlan.setCurrentPlan(PlanReviewTestCurrentPlan);
    this.planReviewTest(page, func);
  }

  /**
   * 言語を指定
   * @param lang
   */
  planReviewTestLanguage(lang: string) {
    this._common.aswContextStoreService.updateAswContextByKey('lang', lang);
    this._translateService.use(lang);
    this.planReviewTestFromPlanList();
  }

  /**
   * pointOfSaleIdを指定して初期表示
   * @param pointOfSaleId 操作オフィスコード
   */
  planReviewTestCreationPOS(pointOfSaleId: string) {
    this._common.aswContextStoreService.updateAswContextByKey('pointOfSaleId', pointOfSaleId);
    this._currentCart.setCurrentCart(PlanReviewTestValidCart);
    this._currentPlan.setCurrentPlan(PlanReviewTestCurrentPlan);
    this._deliveryInformationStoreService.setDeliveryInformationByKey('planReviewInformation', 'isNeedGetCart', false);
    this.planReviewTest('P042', 'R01');
  }

  /**
   * 規約モーダルを表示
   */
  planReviewTestShowAgreement(): void {
    const testCurrentPlan = PlanReviewTestCurrentPlan;

    if (this._common.isNotLogin()) {
      // ローカルプランリストと操作中プランの齟齬により規約が表示されないケースへの対応
      this._fixLocalPlanListForAgreement(testCurrentPlan);
    }

    // プラン作成オフィスと異なるpointOfSaleIdを選定
    const arbitraryPOSIdList = ['LONNH08AA', 'PARNH08AA'];
    const pointOfSaleId = arbitraryPOSIdList.find((option) => option !== testCurrentPlan.creationPointOfSaleId) ?? '';
    this.planReviewTestCreationPOS(pointOfSaleId);
  }

  /**
   * 規約表示用にローカルプランリストを修正する処理
   * @param currentPlan 操作中プラン
   */
  private _fixLocalPlanListForAgreement(currentPlan: CurrentPlanModel): void {
    const localPlanList = this._localPlanService.getLocalPlans();
    const localPlan = localPlanList?.plans?.find((plan) => plan.cartId === currentPlan.cartId);
    const isSameCreationPOS = localPlan?.creationPointOfSaleId === currentPlan.creationPointOfSaleId;

    if (localPlanList && localPlan && !isSameCreationPOS) {
      // ローカルプランで操作中プランが上書きされ、作成オフィスが変わってしまうケースへの対応
      const msg =
        `続行した場合、カートID: ${localPlan.cartId} のローカルプランに対し、以下の変更が行われます。続行しますか？` +
        '\n' +
        `creationPointOfSaleId: ${localPlan.creationPointOfSaleId} → ${currentPlan.creationPointOfSaleId}`;
      if (!window.confirm(msg)) {
        return;
      }
      // 操作中プランのオフィスでローカルプランのオフィスを上書きする
      localPlan.creationPointOfSaleId = currentPlan.creationPointOfSaleId;
      this._localPlanService.setLocalPlans(localPlanList);
    } else if (localPlanList && !localPlan) {
      // ローカルプランリストに操作中プランが含まれず、操作中プラン情報がクリアされてしまうケースへの対応
      const newLocalPlanList = { plans: [...(localPlanList.plans ?? []), currentPlan] };
      this._localPlanService.setLocalPlans(newLocalPlanList);
    }
  }

  /**
   * 操作オフィスのPOSを指定して初期表示
   * @param posCountryCode
   */
  planReviewTestPOSCountryCode(posCountryCode: string) {
    this._common.aswContextStoreService.updateAswContextByKey('posCountryCode', posCountryCode);
    this._currentCart.setCurrentCart(PlanReviewTestValidCart);
    this._currentPlan.setCurrentPlan(PlanReviewTestCurrentPlan);
    this._deliveryInformationStoreService.setDeliveryInformationByKey('planReviewInformation', 'isNeedGetCart', false);
    this.planReviewTest('P042', 'R01');
  }

  /**
   * ペットらくのり画面からエラー連携を受けた場合を再現
   */
  planReviewTestPetError() {
    const errorInfo = [{ errorMsgId: 'E0381', apiErrorCode: 'EBAZ000516' }];
    this._deliveryInformationStoreService.setDeliveryInformationByKey('serviceApplication', 'errorInfo', errorInfo);
    this._currentCart.setCurrentCart(PlanReviewTestValidCart);
    this._currentPlan.setCurrentPlan(PlanReviewTestCurrentPlan);
    this.planReviewTest('P054', 'R01');
  }

  /**
   * アップセルテスト用
   * @param page
   * @param func
   */
  planReviewTestUpsell(page: string, func: string) {
    this._deliveryInformationStoreService.setDeliveryInformationByKey('planReviewInformation', 'isNeedGetCart', false);
    this._createCartStoreService.setCreateCart(PlanReviewTestUpsellCart);
    this.planReviewTest('P030', 'R01');
  }

  /**
   * Bizオフィスでアップセル
   */
  planReviewTestUpsellBizOffice() {
    this._common.aswContextStoreService.updateAswContextByKey('pointOfSaleId', ANA_BIZ_OFFICE_CODE);
    this._createCartStoreService.setCreateCart(PlanReviewTestUpsellCart);
    this._deliveryInformationStoreService.setDeliveryInformationByKey('planReviewInformation', 'isNeedGetCart', false);
    this.planReviewTest('P030', 'R01');
  }

  /**
   * Bizオフィス作成プランで規約を表示
   */
  planReviewTestBizAgreement() {
    this._common.aswContextStoreService.updateAswContextByKey('pointOfSaleId', 'TYONH08DD');
    this._currentCart.setCurrentCart(PlanReviewTestValidCart);
    this._currentPlan.setCurrentPlan({
      ...PlanReviewTestCurrentPlan,
      creationPointOfSaleId: ANA_BIZ_OFFICE_CODE,
    });
    this._deliveryInformationStoreService.setDeliveryInformationByKey('planReviewInformation', 'isNeedGetCart', false);
    this.planReviewTest('P042', 'R01');
  }

  planReviewTest1(page: string, func: string, isCurrent: boolean = true) {
    const data2 = deepCopy(this._currentPlan.CurrentPlanData);
    data2.isUnsaved = false;
    this._currentPlan.updateCurrentPlan(data2);
    const master = this.getDefaultCartData();
    if (isCurrent) {
      this._currentCart.setCurrentCart(master);
    } else {
      this._createCartStoreService.setCreateCart(master);
    }

    this.planReviewTest(page, func);
  }

  planReviewTest2(page: string, func: string, isCurrent: boolean = true) {
    if (isCurrent) {
      this._currentCart.setCurrentCart(this.getDefaultCartData());
    } else {
      this._createCartStoreService.setCreateCart(this.getDefaultCartData());
    }
    this.planReviewTest(page, func);
  }
  planReviewTest3(page: string, func: string, isCurrent: boolean = true) {
    const data = deepCopy(this.getDefaultCartData());
    if (data.data?.plan?.services) {
      const service = data.data?.plan?.services;
      Object.values(service.baggage?.firstBaggage ?? {}).forEach((item) => {
        Object.values(item).forEach((temp) => {
          if (typeof temp !== 'boolean') {
            temp.serviceStatus = '';
          }
        });
      });
      Object.values(service.lounge ?? {}).forEach((item) => {
        Object.values(item).forEach((temp) => {
          if (typeof temp !== 'boolean') {
            temp.serviceStatus = '';
            temp.code = '';
          }
        });
      });
      Object.values(service.meal ?? {}).forEach((item) => {
        Object.values(item).forEach((temp) => {
          if (typeof temp !== 'boolean') {
            temp.appliedMealList.forEach((meal) => {
              meal.code = '';
              meal.statusCode = '';
              if (meal.price) meal.price.total = 0;
            });
          }
        });
      });
    }
    if (data.data?.previousPlan?.services) {
      const service = data.data?.previousPlan?.services;
      Object.values(service.baggage?.firstBaggage ?? {}).forEach((item) => {
        Object.values(item).forEach((temp) => {
          if (typeof temp !== 'boolean') {
            temp.serviceStatus = '';
          }
        });
      });
      Object.values(service.lounge ?? {}).forEach((item) => {
        Object.values(item).forEach((temp) => {
          if (typeof temp !== 'boolean') {
            temp.serviceStatus = '';
            temp.code = '';
          }
        });
      });
      Object.values(service.meal ?? {}).forEach((item) => {
        Object.values(item).forEach((temp) => {
          if (typeof temp !== 'boolean') {
            temp.appliedMealList.forEach((meal) => {
              meal.code = '';
              meal.statusCode = '';
              if (meal.price) meal.price.total = 0;
            });
          }
        });
      });
    }
    console.log(data);

    if (isCurrent) {
      this._currentCart.setCurrentCart(data);
    } else {
      this._createCartStoreService.setCreateCart(data);
    }

    this.planReviewTest(page, func);
  }

  planReviewTest4(page: string, func: string, isCurrent: boolean = true) {
    const data = this.getDefaultCartData();
    const data2 = deepCopy(this._currentPlan.CurrentPlanData);
    data2.isUnsaved = true;
    this._currentPlan.updateCurrentPlan(data2);

    if (isCurrent) {
      this._currentCart.setCurrentCart(data);
    } else {
      this._createCartStoreService.setCreateCart(data);
    }

    this.planReviewTest(page, func);
  }

  planReviewTest5(page: string, func: string, isCurrent: boolean = true) {
    const data: any = this.getDefaultCartData();
    if (data.data?.plan) {
      data.data.plan.orderEligibilities = {
        firstBaggage: {
          isEligible: true,
        },
      };
    }

    if (isCurrent) {
      this._currentCart.setCurrentCart(data);
    } else {
      this._createCartStoreService.setCreateCart(data);
    }

    this.planReviewTest(page, func);
  }

  planReviewTest6(page: string, func: string, isCurrent: boolean = true) {
    const data = this.getDefaultCartData();
    if (data.data?.plan?.travelers && data.data.plan.travelersSummary) {
      data.data.plan.travelers = [];
      data.data.plan.travelersSummary.isRegistered = false;
    }

    if (isCurrent) {
      this._currentCart.setCurrentCart(data);
    } else {
      this._createCartStoreService.setCreateCart(data);
    }

    this.planReviewTest(page, func);
  }

  planReviewTest7(page: string, func: string, isCurrent: boolean = true) {
    const master = this.getDefaultCartData(false);
    if (isCurrent) {
      this._currentCart.setCurrentCart(master);
    } else {
      this._createCartStoreService.setCreateCart(master);
    }

    this.planReviewTest(page, func);
  }

  planReviewTest(page: string, func: string) {
    this._common.aswCommonStoreService.updateAswCommon({ functionId: func, pageId: page });
    this._deliveryInformationStoreService.updateDeliveryInformation({
      planReviewInformation: {
        isNeedGetCart: false,
      },
    });
    this._sleep(() => {
      this._router.navigateByUrl(RoutesResRoutes.PLAN_REVIEW);
    });
  }

  planReviewTestAgree(country: string, page: string, func: string) {
    //this.isCameFromLater = previousPage === 'R01P070' || previousPage === 'R01P080';
    this._common.aswContextStoreService.updateAswContext({ posCountryCode: country });
    this._common.aswCommonStoreService.updateAswCommon({ functionId: func, pageId: page });
    if (isStringEmpty(func)) {
      this._currentCart.setCurrentCart(PlanReviewTestCart1);
    } else {
      this._createCartStoreService.setCreateCart(PlanReviewTestCart1);
    }

    this._deliveryInformationStoreService.updateDeliveryInformation({
      planReviewInformation: {
        isNeedGetCart: false,
      },
    });
    this._sleep(() => {
      this._router.navigateByUrl(RoutesResRoutes.PLAN_REVIEW);
    });
  }

  getDefaultCartData(isAutoComplete = true): CurrentCartState {
    const master: CurrentCartState = deepCopy(PlanReviewTestCart1);
    const update1: CurrentCartState = getLoungeTestData(master);
    const update2: CurrentCartState = getMealTestData(update1);
    const update3: CurrentCartState = deepCopy(getBaggageTestData(update2));
    if (isAutoComplete) {
      update3.data?.plan?.travelers
        ?.filter((trav) => isStringEmpty(trav.names?.[0]?.firstName))
        .forEach((trav) => {
          trav.names = [
            {
              firstName: 'NO FIRST NAME',
              lastName: 'NO LAST NAME',
            },
          ];
        });
    }
    return update3;
  }
}
